// Bi-directional sync utilities between weekly and monthly edits.
// Rule: latest edit wins globally (timestamp-based). Day-accurate month<->week mapping uses daily_values.

import { rollupWeeklyToMonthly } from './PlannerRollup'
import { deriveMonthlyMarkdownValue, sumMonthlyMarkdownPlan } from './PlannerMonthlyMarkdown'

/**
 * Roll monthly edits back down into weekly edits using day weights.
 *
 * Strategy:
 * - For each edited month, compute delta multipliers against base month values.
 * - Distribute into overlapping weeks by day weights (days of week that fall in that month).
 * - For percentage fields, we re-derive weekly plan% based on weekly actual sales.
 */
const remapMonthEditsForAllocate = ({ monthlyBase, monthlyEdits }) => {
  const baseByMonth = new Map()
  ;(monthlyBase || []).forEach((m) => {
    const em = Number(m?.epochMonth)
    if (Number.isFinite(em)) baseByMonth.set(em, m)
  })

  const months = Array.from(baseByMonth.keys()).sort((a, b) => a - b)
  if (!months.length) return monthlyEdits || {}

  // Build effective month sales after edits (only planPercent edits affect sales totals here).
  const baseAnnualSales = months.reduce((acc, em) => acc + Number(baseByMonth.get(em)?.salesPlan ?? 0), 0)
  const out = { ...(monthlyEdits || {}) }

  // Determine desired sales per month from planPercent edits; otherwise keep base.
  const desiredSales = months.map((em) => {
    const baseSales = Number(baseByMonth.get(em)?.salesPlan ?? 0)
    const e = out?.[String(em)]
    const pct = (e && typeof e.planPercent !== 'undefined') ? Number(e.planPercent) : null
    if (pct === null || Number.isNaN(pct)) return baseSales
    return (baseAnnualSales * pct) / 100
  })

  const desiredAnnual = desiredSales.reduce((a, b) => a + b, 0)
  const delta = desiredAnnual - baseAnnualSales

  if (delta !== 0 && months.length > 1) {
    // Offset into months 2..12 proportionally.
    const tailBase = months.slice(1).reduce((acc, em) => acc + Number(baseByMonth.get(em)?.salesPlan ?? 0), 0)
    if (tailBase !== 0) {
      const scale = (tailBase - delta) / tailBase
      months.slice(1).forEach((em) => {
        const baseSales = Number(baseByMonth.get(em)?.salesPlan ?? 0)
        const sales = baseSales * scale
        const pct = baseAnnualSales ? (sales / baseAnnualSales) * 100 : 0
        const prev = out[String(em)] || {}
        out[String(em)] = { ...prev, planPercent: pct, ts: prev.ts || Date.now() }
      })
    }
  }

  return out
}

export const rollupMonthlyToWeekly = ({
  weeklyBase,
  monthlyBase,
  monthlyEdits,
  weekToMonthDayCounts,
  calcMode = 'allocate',
  weeklyActualSalesByWeek = {},
}) => {
  const weekEditsOut = { ...(weeklyBase?.editsByWeek || {}) }

  const effectiveMonthlyEdits = (calcMode === 'allocate')
    ? remapMonthEditsForAllocate({ monthlyBase, monthlyEdits })
    : (monthlyEdits || {})

  // Compute a base weekly plan% shape from existing weekly edits (or fallback to 0).
  const baseWeekPlanPct = {}
  Object.entries(weekEditsOut).forEach(([ew, v]) => {
    const pct = Number(v?.planPercent)
    if (Number.isFinite(pct)) baseWeekPlanPct[String(ew)] = pct
  })

  const monthByEm = new Map()
  ;(monthlyBase || []).forEach((m) => {
    const em = Number(m?.epochMonth)
    if (Number.isFinite(em)) monthByEm.set(em, m)
  })

  const annualMarkdownPlan = sumMonthlyMarkdownPlan((monthlyBase || []).map((row) => {
    const em = Number(row?.epochMonth)
    const monthEdit = Number.isFinite(em) ? monthlyEdits?.[String(em)] : null
    return {
      ...row,
      markdownPlan: typeof monthEdit?.markdownPlan !== 'undefined'
        ? Number(monthEdit.markdownPlan)
        : Number(row?.markdownPlan ?? 0),
    }
  }))

  // Build reverse map: epochMonth -> [{ew, daysInThatMonthWithinWeek, totalDaysInWeek}]
  const monthToWeekWeights = new Map()
  for (const [ew, mMap] of (weekToMonthDayCounts || new Map()).entries()) {
    const totalDays = Array.from(mMap.values()).reduce((a, b) => a + b, 0) || 0
    for (const [em, days] of mMap.entries()) {
      if (!monthToWeekWeights.has(em)) monthToWeekWeights.set(em, [])
      monthToWeekWeights.get(em).push({ ew: String(ew), days, totalDays })
    }
  }

  Object.entries(effectiveMonthlyEdits || {}).forEach(([emStr, edit]) => {
    const em = Number(emStr)
    if (!Number.isFinite(em) || !edit) return

    const baseMonth = monthByEm.get(em)
    if (!baseMonth) return

    const ts = edit?.ts || Date.now()

    const newMonthPlanPct = typeof edit.planPercent !== 'undefined' ? Number(edit.planPercent) : null
    const newMonthMdPct = typeof edit.markdownPlanPercent !== 'undefined' ? Number(edit.markdownPlanPercent) : null
    const newMonthMdDollars = typeof edit.markdownPlan !== 'undefined' ? Number(edit.markdownPlan) : null

    // Determine monthly markdown dollars to allocate.
    const baseMonthMd = Number(baseMonth?.markdownPlan ?? 0)

    const targetMonthMd = (newMonthMdDollars !== null && Number.isFinite(newMonthMdDollars))
      ? newMonthMdDollars
      : (newMonthMdPct !== null && Number.isFinite(newMonthMdPct))
        ? deriveMonthlyMarkdownValue({
          markdownPlanPercent: newMonthMdPct,
          annualMarkdownPlan,
        })
        : baseMonthMd

    const weights = monthToWeekWeights.get(em) || []

    // Preserve weekly shape: compute a scale factor for overlapping weeks.
    let scale = null
    if (newMonthPlanPct !== null) {
      const denom = weights.reduce((acc, { ew, days, totalDays }) => {
        const w = totalDays ? days / totalDays : 0
        const basePct = Number(baseWeekPlanPct[String(ew)] ?? weekEditsOut[String(ew)]?.planPercent ?? 0)
        return acc + w * (Number.isFinite(basePct) ? basePct : 0)
      }, 0)
      if (denom !== 0) scale = newMonthPlanPct / denom
    }

    weights.forEach(({ ew, days, totalDays }) => {
      const w = totalDays ? days / totalDays : 0
      if (!w) return

      const prev = weekEditsOut[ew] || {}
      const prevTs = prev?.ts || 0
      if (ts < prevTs) return // latest edit wins

      const out = { ...prev, ts }

      if (newMonthPlanPct !== null) {
        const basePct = Number(baseWeekPlanPct[String(ew)] ?? prev.planPercent ?? 0)
        if (Number.isFinite(scale) && Number.isFinite(basePct)) {
          out.planPercent = basePct * scale
        } else {
          const existing = Number.isFinite(basePct) ? basePct : null
          out.planPercent = existing === null ? newMonthPlanPct : existing * (1 - w) + newMonthPlanPct * w
        }
      }

      // Allocate markdown dollars to week by day-weight, then compute weekly markdown %.
      const mdWeek = targetMonthMd * w
      const weeklyActualSales = Number(weeklyActualSalesByWeek?.[Number(ew)] ?? weeklyActualSalesByWeek?.[String(ew)] ?? 0)
      const salesWeek = weeklyActualSales ? ((Number(out.planPercent ?? 0) / 100) * weeklyActualSales) : 0
      out.markdownPlanPercent = salesWeek ? (mdWeek / salesWeek) * 100 : 0

      if (typeof edit.planInvBOM !== 'undefined') {
        // Inventory BOM locked in Allocate when sales change; keep weekly inv edits only.
        const bom = Number(edit.planInvBOM)
        if (Number.isFinite(bom)) out.planInvBOM = bom
      }

      // Weekly S/S is not edited.

      weekEditsOut[ew] = out
    })
  })

  return weekEditsOut
}

/**
 * One-step bidirectional reconciliation producing effective mounts + weeks.
 */
export function reconcileWeeklyMonthly({
  monthlyBase,
  monthlyEdits,
  weeklyEdits,
  weekToMonthDayCounts,
  weeklyActualSalesByWeek,
}) {
  // 1) Weekly -> Monthly derived.
  const monthlyEffective = rollupWeeklyToMonthly({
    monthlyBase,
    monthlyEdits,
    weeklyEdits,
    weekToMonthDayCounts,
    weeklyActualSalesByWeek,
  })

  // 2) Monthly -> Weekly derived (only for months that have newer edits than corresponding weeks).
  const weeklyEffective = rollupMonthlyToWeekly({
    weeklyBase: { editsByWeek: weeklyEdits },
    monthlyBase,
    monthlyEdits,
    weekToMonthDayCounts,
  })

  return { monthlyEffective, weeklyEffective }
}

// Prefer named exports.
// (No default export.)
